import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { AppShell } from "@/components/TopNav";
import { PageHeader, Delta, EmptyState, HalalBadge, LiveBadge } from "@/components/common";
import { MarketsTabs } from "@/components/markets/MarketsTabs";
import { TradeSheet } from "@/components/order/TradeSheet";
import { Sparkline } from "@/components/Charts";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { fmtCcy, fmtUSD, timeAgo } from "@/lib/format";
import { CalendarClock, Clock } from "lucide-react";

const EXCHANGES = [
  { code: "SA", label: "Tadawul" }, { code: "ADX", label: "ADX" }, { code: "DFM", label: "DFM" },
  { code: "QE", label: "Qatar" }, { code: "KW", label: "Kuwait" }, { code: "BH", label: "Bahrain" },
  { code: "OM", label: "Oman" },
];

export default function TradeGCC() {
  const { t } = useApp();
  const navigate = useNavigate();
  const [market, setMarket] = useState("SA");
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [book, setBook] = useState(null);
  const [actions, setActions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [idx, setIdx] = useState(null);
  const [ticket, setTicket] = useState({ open: false, symbol: null });

  const selectSym = useCallback(async (sym) => {
    setSelected(sym);
    const [b, a] = await Promise.all([api.get(`/market/orderbook/${sym}`), api.get(`/market/corporate-actions/${sym}`)]);
    setBook(b.data); setActions(a.data);
  }, []);
  const loadList = useCallback(async (m) => {
    const { data } = await api.get(`/market/instruments?market=${m}`);
    setList(data);
    if (data[0]) selectSym(data[0].symbol);
  }, [selectSym]);
  const loadIndex = useCallback(async (m) => { try { const { data } = await api.get(`/market/index/${m}`); setIdx(data); } catch (e) { console.error(e); } }, []);
  const loadOrders = useCallback(async () => { const { data } = await api.get("/orders?source=gcc"); setOrders(data); }, []);
  useEffect(() => { loadList(market); loadIndex(market); loadOrders(); }, [market, loadList, loadIndex, loadOrders]);
  useEffect(() => { const iv = setInterval(() => { loadList(market); loadIndex(market); if (selected) selectSym(selected); }, 6000); return () => clearInterval(iv); }, [market, selected, loadList, loadIndex, selectSym]);

  const sel = list.find((s) => s.symbol === selected);

  return (
    <AppShell>
      <PageHeader title={t("tr_gcc_title")} subtitle="Saudi · UAE · Qatar · Kuwait · Bahrain · Oman multi-exchange access" action={<LiveBadge />} testid="trade-gcc-header" />

      <MarketsTabs />

      <Tabs value={market} onValueChange={setMarket} className="mb-4" data-testid="gcc-exchange-tabs">
        <TabsList className="flex-wrap h-auto">{EXCHANGES.map((e) => <TabsTrigger key={e.code} value={e.code} data-testid={`gcc-tab-${e.code}`}>{e.label}</TabsTrigger>)}</TabsList>
      </Tabs>

      {idx?.index && (
        <Card className="p-4 sm:p-5 shadow-premium card-premium mb-4 sm:mb-5" data-testid="gcc-index-header">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-6 flex-wrap">
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">{idx.index.name} <span className="opacity-60">({idx.index.code})</span></div>
                <div className="flex items-baseline gap-3 mt-1">
                  <span className="text-2xl sm:text-3xl font-semibold numeric" data-testid="gcc-index-value">{idx.index.value.toLocaleString()}</span>
                  <Delta value={idx.index.change_pct} />
                  <span className={`numeric text-sm ${idx.index.change_points >= 0 ? "text-emerald-600" : "text-red-600"}`}>{idx.index.change_points >= 0 ? "+" : ""}{idx.index.change_points} pts</span>
                </div>
              </div>
              <div className="hidden sm:flex items-center gap-4 text-xs">
                <div><div className="text-muted-foreground">Advancers</div><div className="numeric text-emerald-600 font-medium">{idx.index.advancers}</div></div>
                <div><div className="text-muted-foreground">Decliners</div><div className="numeric text-red-600 font-medium">{idx.index.decliners}</div></div>
                <div><div className="text-muted-foreground">Listed</div><div className="numeric font-medium">{idx.index.constituents}</div></div>
              </div>
              <div className="hidden md:block w-40 lg:w-56" data-testid="gcc-index-sparkline">
                <Sparkline data={idx.index.spark} color={idx.index.change_pct >= 0 ? "#86A230" : "#C2413A"} height={44} />
              </div>
            </div>
            <div className="flex items-center gap-2" data-testid="gcc-market-status">
              <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${idx.status.is_open ? "text-emerald-700" : "text-muted-foreground"}`}
                style={{ background: idx.status.is_open ? "rgba(23,185,120,0.14)" : "rgba(100,116,139,0.12)" }}>
                <span className={`h-2 w-2 rounded-full ${idx.status.is_open ? "bg-emerald-500 live-dot" : "bg-slate-400"}`} />
                {idx.status.is_open ? "Open" : "Closed"}
              </span>
              <div className="text-xs text-muted-foreground leading-tight">
                <div className="flex items-center gap-1"><Clock className="h-3 w-3" />{idx.status.hours} · {idx.status.days_label}</div>
                <div>{idx.status.local_time} {idx.status.tz_label} · {idx.status.next_event}</div>
              </div>
            </div>
          </div>
        </Card>
      )}

      <div className="grid gap-4 sm:gap-5 lg:grid-cols-3">
        <Card className="p-4 shadow-premium min-w-0">
          <div className="text-sm font-medium mb-3">Listed Securities</div>
          <div className="space-y-2 max-h-[520px] overflow-auto">
            {list.map((s) => (
              <div key={`gcc-${s.symbol}`} onClick={() => selectSym(s.symbol)}
                className={`rounded-lg border px-3 py-2 cursor-pointer transition-colors ${selected === s.symbol ? "bg-secondary border-[color:var(--tharwy-lime)]" : "hover:bg-accent"}`}
                data-testid={`gcc-list-${s.symbol}`}>
                <div className="flex items-center justify-between">
                  <div><div className="font-medium text-sm flex items-center gap-2">{s.symbol.split(".")[0]} {s.halal && <HalalBadge />}</div>
                    <div className="text-xs text-muted-foreground truncate max-w-[140px]">{s.name}</div></div>
                  <div className="text-end"><div className="numeric text-sm">{fmtCcy(s.price, s.currency)}</div><div className="numeric text-[11px] text-muted-foreground">≈ {fmtUSD(s.price_usd)}</div><Delta value={s.change_pct} className="text-xs" /></div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-4 shadow-premium min-w-0">
          {sel ? (<>
            <div className="flex items-start justify-between">
              <div><div className="font-semibold text-lg flex items-center gap-2">{sel.symbol.split(".")[0]} {sel.halal && <HalalBadge />}</div>
                <div className="text-xs text-muted-foreground">{sel.name}</div></div>
              <div className="text-end"><div className="numeric text-xl font-semibold">{fmtCcy(sel.price, sel.currency)}</div><div className="numeric text-xs text-muted-foreground">≈ {fmtUSD(sel.price_usd)}</div><Delta value={sel.change_pct} /></div>
            </div>
            <Button className="w-full mt-3" onClick={() => setTicket({ open: true, symbol: sel.symbol })} data-testid="gcc-order-ticket-open">Buy / Sell</Button>

            <div className="mt-4">
              <div className="text-sm font-medium mb-2">Depth of Market (Level 1)</div>
              <div className="grid grid-cols-2 gap-3 text-xs" data-testid="gcc-depth-ladder">
                <div><div className="text-emerald-600 font-medium mb-1">Bids</div>
                  {book?.bids?.map((b, i) => (<div key={`bid-${b.price}-${i}`} className="flex justify-between numeric py-1 border-b"><span>{fmtCcy(b.price, sel.currency)}</span><span className="text-muted-foreground">{b.size.toLocaleString()}</span></div>))}</div>
                <div><div className="text-red-600 font-medium mb-1">Asks</div>
                  {book?.asks?.map((a, i) => (<div key={`ask-${a.price}-${i}`} className="flex justify-between numeric py-1 border-b"><span>{fmtCcy(a.price, sel.currency)}</span><span className="text-muted-foreground">{a.size.toLocaleString()}</span></div>))}</div>
              </div>
            </div>
          </>) : <EmptyState title="Select a security" />}
        </Card>

        <div className="min-w-0 space-y-4 sm:space-y-5">
          <Card className="p-4 shadow-premium">
            <div className="flex items-center gap-2 text-sm font-medium mb-3"><CalendarClock className="h-4 w-4" />Corporate Actions</div>
            {actions.length ? <div className="space-y-2">{actions.map((a, i) => (
              <div key={`${a.type}-${a.ex_date}-${i}`} className="rounded-lg border p-3">
                <div className="flex items-center justify-between"><span className="font-medium text-sm">{a.type}</span><Badge variant="secondary">{a.status}</Badge></div>
                <div className="text-xs text-muted-foreground mt-1">{a.detail}</div>
                <div className="text-xs text-muted-foreground mt-0.5">Date: {a.ex_date}</div>
              </div>))}</div> : <EmptyState title="None" />}
          </Card>
          <Card className="p-4 shadow-premium">
            <div className="text-sm font-medium mb-3">GCC Order History</div>
            {orders.length ? (
              <div className="overflow-x-auto">
              <Table><TableHeader><TableRow><TableHead>{t("tr_symbol")}</TableHead><TableHead>Side</TableHead><TableHead className="text-end">{t("cj_qty")}</TableHead></TableRow></TableHeader>
                <TableBody>{orders.slice(0, 8).map((o) => (<TableRow key={o.id}><TableCell className="font-medium">{o.symbol.split(".")[0]}</TableCell><TableCell><Badge variant={o.side === "buy" ? "default" : "destructive"} className="capitalize">{o.side}</Badge></TableCell><TableCell className="text-end numeric">{o.qty}</TableCell></TableRow>))}</TableBody></Table>
              </div>
            ) : <EmptyState title="No GCC orders" />}
          </Card>
        </div>
      </div>

      <TradeSheet open={ticket.open} onOpenChange={(v) => setTicket({ ...ticket, open: v })} symbol={ticket.symbol} source="gcc" onDone={() => { loadOrders(); if (selected) selectSym(selected); }} />
    </AppShell>
  );
}
